import React, { useEffect, useRef } from 'react'
import {
  Redirect,
  Route,
  RouteProps,
  Switch,
  useHistory,
} from 'react-router-dom'

import {
  InformationScreenType,
  ScreenPayload,
  SessionStatus,
} from '@ally/transmitigator'
import {
  AllyRedirect,
  DocumentTitle,
  useLogRocketTracking,
} from '@ally/ally-login'

import { FeatureFlag } from './Utils'
import log from './whisper'
import { useTransmit } from './TransmitContext'
import {
  ForgotPassword,
  NewPassword,
  PasswordUpdated,
  RegisterDevice,
  SecurityWelcome,
  ExternalLogins,
  SetupComplete,
  SetupSecurity,
  TemporarilyUnavailable,
  EnrollCustomer,
  VerifyCustomerInfo,
  CredentialCreation,
  Confirmation,
  VerifyMortgage,
  GuestEnrollCustomer,
  GuestConfirmation,
  GuestCredentialCreation,
  NoMfaOptionError,
  SelectAccount,
  ForgotUsernameConfirmation,
  VerifyAutoTypeSelect,
  VerifyAutoInput,
  AutoAltMfaMaxRetries,
  ForgotUsernameEmailLookup,
  RecoveryOptionStep,
  ConfirmSSN,
  BankToAutoLinking,
  LoginAccountTypeSelector,
  AutoToBankLinking,
  AddAutoAccountSuccess,
  AddAutoAccountFailure,
  AddAutoAccount,
  ESignWrapper,
  LoginWrapper,
  OBSAWrapper,
  SecureConsentWrapper,
  SendCodeWrapper,
  VerifyCodeWrapper,
  UpdateUsername,
  AutoForgotUsernamePassword,
  AutoLinkFailedMaxRetries,
  LinkingSuccess,
  AddAutoAccountGenericFailure,
} from './Components'
import { RedirectContext, useRedirectPathValue } from './RedirectContext'
import { RouteSkeleton } from './RouteSkeleton'
import { AuthPayload, useRedirectPath } from './Redirects'
import { useHostServices } from './HostServices'

const routes: Partial<Record<ScreenPayload['type'], string>> = {
  ESign: '/security/consent/esign',
  ForgotPassword: '/forgot-password',
  ForgotUsername: '/forgot-username',
  ResetPassword: '/reset-password',
  OBSA: '/security/consent/obsa',
  NewPassword: '/security/new-password',
  Password: '/',
  PasswordUpdated: '/security/password-updated',
  RegisterDevice: '/security/register-device',
  RecoveryOptions: '/forgot-password/recovery-options',
  MFASuppressionNoOptions: '/security/send-code',
  SendCode: '/security/send-code',
  SetupComplete: '/security/setup-complete',
  SetupSecurity: '/security/setup',
  SetupWelcome: '/security/welcome',
  TemporarilyUnavailable: '/temp-unavailable',
  VerifyCode: '/security/verify-code',
  Enroll: '/enroll',
  VerifyCustomerInfo: '/enroll/verify-customer',
  EnrollAutoTypeSelect: '/enroll/additional-verification-type',
  EnrollAutoInput: '/enroll/additional-verification',
  AutoAltMfaMaxRetries: '/security/auto-mfa-max-retries',
  EnrollCredentialCreation: '/enroll/credential-creation',
  EnrollConfirmation: '/enroll/confirmation',
  GuestCustomerInfo: '/new-user',
  GuestCredentialCreation: '/new-user/credential-creation',
  GuestConsent: '/security/electronic-consent',
  GuestConfirmation: '/new-user/confirmation',
  ConfirmSSN: '/security/forgot-username/confirm-identity',
  SelectAccount: '/security/forgot-username/select-account',
  ForgotUsernameConfirmation: '/security/forgot-username/username',
  BankToAutoLinking: '/security/account-linking/bank-to-auto',
  LoginAccountTypeSelector: '/security/account-linking/select-account-type',
  AutoToBankLinking: '/security/account-linking/auto-to-bank',
  UpdateUsername: '/security/account-linking/update-username',
  AddAutoAccountSuccess: '/security/account-linking/auto-success',
  AddAutoAccountFailure: '/security/account-linking/auto-failure',
  AddAutoAccount: '/security/account-linking/add-auto-account',
  AutoForgotUsernamePassword: '/security/account-linking/forgot-credentials',
  AutoLinkFailedMaxRetries:
    '/security/account-linking/auto-link-failed-max-retries',
  LinkingSuccess: '/security/account-linking/success',
  AddAutoAccountGenericFailure: '/security/account-linking/failure',
}

type RouteComponent = keyof typeof routes

interface JourneyRouteProps extends RouteProps {
  screens: RouteComponent[]
}

const AllyRedirectContainer: React.FC = () => {
  const { redirectPath } = useRedirectPathValue()

  return <AllyRedirect redirectPath={redirectPath} />
}

const AuthenticatedTransition: React.FC = () => {
  const {
    sessionStatus: { status },
  } = useTransmit()

  if (status !== 'Authenticated' && status !== 'AuthenticatedWithTicket') {
    return <Redirect to="/" />
  }

  return <AllyRedirectContainer />
}

/**
 * Creates a React component that either:
 *   - Redirects to login (if the current screen doesn't match on of `screens`).
 *   - Renders the children otherwise.
 *
 * This solves 2 issues:
 *   - When clicking back from mid-login journey it ensures the user is
 *     redirected to the login page.
 *   - When directly accessing a "login flow route" from the URL it redirects
 *     the user to login page.
 */

const JourneyRoute: React.FC<JourneyRouteProps> = ({
  screens,
  children,
  ...props
}) => {
  const {
    activeScreen: { type },
  } = useTransmit()
  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <Route {...props}>
      {screens.includes(type) ? children : <AuthenticatedTransition />}
    </Route>
  )
}

interface TransmitRouteWrapperProps {
  navigateAction: () => void
}

/**
 * Wraps around an entry point of a Transmit journey that can be manually
 * visited by a user utilizing browser navigation. This component ensures the activeScreen state
 * is reset. This removes any errors or other contextual messages that were sitting
 * around at previous visits. An example of a bug this solves:
 *      - User visits login
 *      - User clicks Forgot Password link
 *      - User clicks the Ally Logo (going back to login)
 *      - User mistypes password (which updates the state with an error indication)
 *      - User clicks *back* in their browser bringing them back to Forgot Password
 *      - User clicks *forward* in their browser
 *      - Error message from before is still there! (because Transmitigator was unaware of state changes)
 *
 * This wrapper is not needed outside of a routing context, since the only way the user can navigate
 * is through navigate actions provided by Transmitigator, or by side effects of CIAM
 * directed routing.
 *
 * You should use a key on each usage of this wrapper, otherwise React reconciliation will aggressively try
 * to re-use this component even when React Router switches routes. This lead to a bug where the
 * useEffect mounting action only ran one time.
 *
 * @param navigateAction - An action that updates the activeScreen state that aligns with the child view.
 */
const TransmitRouteWrapper: React.FC<TransmitRouteWrapperProps> = ({
  navigateAction,
  children,
}) => {
  const {
    sessionStatus: { status },
  } = useTransmit()

  // This can't be in the dependency array right now since its being passed from a context provider
  React.useEffect(() => {
    navigateAction()
  }, [])

  if (status === 'Rehydrating' || status === 'Redirecting') {
    return <RouteSkeleton />
  }

  if (
    status === 'Authenticated' ||
    status === 'LegacyAuthenticated' ||
    status === 'AuthenticatedWithTicket'
  ) {
    return <AllyRedirectContainer />
  }

  return <>{children}</>
}

interface AllyLoginRouterProps {
  defaultAuthRoute: string
  sessionRoutes?: string[] | null
}

const getAuthPayload = (
  sessionStatus: SessionStatus,
  activeScreen: ScreenPayload,
): AuthPayload | null => {
  switch (sessionStatus.status) {
    case 'Authenticated':
      return {
        type: 'Session',
        data: sessionStatus.data,
      }
    case 'LegacyAuthenticated':
      return {
        type: 'Session',
        data: activeScreen.payload,
      }
    case 'AuthenticatedWithTicket':
      return {
        type: 'Ticket',
        data: sessionStatus.ticketID,
      }
    default:
      return null
  }
}

/**
 * A react-router-dom Switch component that decides which component to render
 * based on the value of `activeScreen`. If no component for the active screen
 * is found, this will render nothing and the route to render is left to the
 * consumer. You should pass your application's routes as children to this
 * component.
 *
 * MUST be wrapped in <AllyLoginProvider />.
 *
 * @param props.onUserAuthenticated - If provided, will be called when
 * authentication is completed successfully. If missing, the default behavior
 * is to push to /dashboard
 */
export const AllyLoginRouter: React.FC<AllyLoginRouterProps> = ({
  defaultAuthRoute,
  sessionRoutes = null,
  children,
}) => {
  const { logger } = useTransmit()
  const { featureFlags } = useHostServices()

  const isGuestEnabled: boolean = featureFlags.variation(
    FeatureFlag.GuestExperience,
    false,
  )

  useLogRocketTracking(logger, 'login-transmit-init')

  const history = useHistory()

  const {
    redirects: isEnabled,
    activeScreen,
    sessionStatus,
    navigateToLogin,
    navigateToResetPassword,
    navigateToForgotPassword,
    navigateToForgotUsername,
    navigateToOleCustomer,
    navigateToGuestOle,
    ticketID,
    clearTicketID,
  } = useTransmit()

  const redirectPath = useRedirectPath({
    authPayload: getAuthPayload(sessionStatus, activeScreen),
    defaultAuthRoute,
    sessionRoutes,
    isEnabled,
  })

  const handleStateChange = (): void => {
    const route = routes[activeScreen.type]

    if (history.location.pathname === route) {
      /* istanbul ignore next */
      log.debug({
        message: [
          'State change on same screen, no need for route change',
          activeScreen,
        ],
      })
    } else if (history.location.pathname === '/security/error') {
      // TODO: Find a better way to do this
      log.debug({
        message:
          'User is on security error screen, preventing automatic route change',
      })
    } else if (route) {
      log.debug({
        message: [
          'Changing route due to activeScreen change',
          activeScreen,
          route,
        ],
      })

      history.push(route)
    } else {
      /* istanbul ignore next */
      log.debug({ message: ['No match for screen', activeScreen] })
    }
  }

  // Used to prevent the subsequent useEffect from running on initial render which
  // in certain circumstances can cause an infinite loop
  const isMounted = useRef(false)
  useEffect(() => {
    if (isMounted.current) {
      handleStateChange()
    } else {
      isMounted.current = true
    }
  }, [history, activeScreen])

  log.debug({
    message: [
      'Active State:',
      {
        activeScreen,
        sessionStatus,
      },
    ],
  })

  const handleNavigateAction = (navigateAction: () => void) => (): void => {
    // If we have a ticketID and it is of type sf-er (Storefront Error) or sf-nm (Storefront Needs More)
    // we need to disable the initial navigate action so that when SF redirects
    // to the route `/` we still use the activeScreen to go to OTP
    const isSFNM = /^sf-(er|nm)|au-mc|pr-/.test(ticketID ?? '')
    if (!isSFNM) navigateAction()
    clearTicketID()
    if (isSFNM) {
      handleStateChange()
    }
  }

  return (
    <RedirectContext.Provider value={redirectPath}>
      <Switch>
        {/* ------------------ DIRECTLY ACCESSIBLE ROUTES -------------------- */}
        <Route path="/" exact>
          <DocumentTitle>
            <TransmitRouteWrapper
              key="login"
              navigateAction={handleNavigateAction(navigateToLogin)}
            >
              <LoginWrapper />
              <ExternalLogins />
            </TransmitRouteWrapper>
          </DocumentTitle>
        </Route>
        <Route path="/forgot-password" exact>
          <DocumentTitle title="Forgot Password">
            <TransmitRouteWrapper
              key="forgot-password"
              navigateAction={navigateToForgotPassword}
            >
              <ForgotPassword />
            </TransmitRouteWrapper>
          </DocumentTitle>
        </Route>
        <Route path="/forgot-username" exact>
          <DocumentTitle title="Forgot Username">
            <TransmitRouteWrapper
              key="forgot-username"
              navigateAction={navigateToForgotUsername}
            >
              <ForgotUsernameEmailLookup />
            </TransmitRouteWrapper>
          </DocumentTitle>
        </Route>
        <JourneyRoute
          screens={['RecoveryOptions']}
          path="/forgot-password/recovery-options"
          exact
        >
          <DocumentTitle title="Forgot Password">
            <RecoveryOptionStep />
          </DocumentTitle>
        </JourneyRoute>
        <Route path="/reset-password" exact>
          <DocumentTitle title="Reset Password">
            <TransmitRouteWrapper
              key="reset-password"
              navigateAction={navigateToResetPassword}
            >
              <ForgotPassword />
            </TransmitRouteWrapper>
          </DocumentTitle>
        </Route>
        <Route path="/enroll" exact>
          <DocumentTitle title="Online Enrollment">
            <TransmitRouteWrapper
              key="online-enrollment"
              navigateAction={navigateToOleCustomer}
            >
              <EnrollCustomer />
            </TransmitRouteWrapper>
          </DocumentTitle>
        </Route>
        <JourneyRoute
          screens={['VerifyCustomerInfo']}
          path="/enroll/verify-customer"
          exact
        >
          <DocumentTitle title="Verify Customer Info">
            <VerifyCustomerInfo />
          </DocumentTitle>
        </JourneyRoute>
        <JourneyRoute
          screens={['EnrollCredentialCreation']}
          path="/enroll/credential-creation"
          exact
        >
          <DocumentTitle title="Credential Creation">
            <CredentialCreation />
          </DocumentTitle>
        </JourneyRoute>
        <JourneyRoute
          screens={['EnrollAutoTypeSelect']}
          path="/enroll/additional-verification-type"
          exact
        >
          <DocumentTitle title="Additional Verification">
            <VerifyAutoTypeSelect />
          </DocumentTitle>
        </JourneyRoute>
        <JourneyRoute
          screens={['EnrollAutoInput']}
          path="/enroll/additional-verification"
          exact
        >
          <DocumentTitle title="Additional Verification">
            <VerifyAutoInput />
          </DocumentTitle>
        </JourneyRoute>
        <JourneyRoute
          screens={['EnrollConfirmation']}
          path="/enroll/confirmation"
          exact
        >
          <DocumentTitle title="Online Enrollment">
            <Confirmation />
          </DocumentTitle>
        </JourneyRoute>
        {/* NOTE: this should be journey route */}
        <Route path="/enroll/verify-loan" exact>
          <DocumentTitle title="Online Enrollment">
            <TransmitRouteWrapper
              key="enroll-verify-loan"
              navigateAction={navigateToOleCustomer}
            >
              <VerifyMortgage />
            </TransmitRouteWrapper>
          </DocumentTitle>
        </Route>
        <Route path="/new-user" exact>
          <DocumentTitle title="Introduce Yourself">
            <TransmitRouteWrapper
              key="guest-ole"
              navigateAction={navigateToGuestOle}
            >
              <GuestEnrollCustomer isGuestEnabled={isGuestEnabled} />
            </TransmitRouteWrapper>
          </DocumentTitle>
        </Route>
        <JourneyRoute
          screens={['GuestCredentialCreation']}
          path="/new-user/credential-creation"
          exact
        >
          <DocumentTitle title="Getting In">
            <GuestCredentialCreation />
          </DocumentTitle>
        </JourneyRoute>
        <JourneyRoute
          screens={['GuestConsent']}
          path="/security/electronic-consent"
          exact
        >
          <DocumentTitle title="Electronic Consent and Terms of Use">
            <SecureConsentWrapper />
          </DocumentTitle>
        </JourneyRoute>
        <JourneyRoute
          screens={['GuestConfirmation']}
          path="/new-user/confirmation"
          exact
        >
          <DocumentTitle title="Confirmation">
            <GuestConfirmation />
          </DocumentTitle>
        </JourneyRoute>
        <Route path="/security/error" exact>
          <DocumentTitle title="Call Us">
            <NoMfaOptionError />
          </DocumentTitle>
        </Route>
        {/* --------------------- LOGIN JOURNEY ROUTES ----------------------- */}
        <JourneyRoute
          screens={[InformationScreenType.SetupWelcome]}
          path="/security/welcome"
          exact
        >
          <DocumentTitle title="Welcome">
            <SecurityWelcome />
          </DocumentTitle>
        </JourneyRoute>
        <JourneyRoute exact path="/security/setup" screens={['SetupSecurity']}>
          <DocumentTitle title="Setup Security">
            <SetupSecurity />
          </DocumentTitle>
        </JourneyRoute>
        <JourneyRoute
          exact
          path="/security/register-device"
          screens={['RegisterDevice']}
        >
          <DocumentTitle title="Register Device">
            <RegisterDevice />
          </DocumentTitle>
        </JourneyRoute>
        <JourneyRoute
          exact
          path="/security/setup-complete"
          screens={['SetupComplete']}
        >
          <DocumentTitle title="Security Setup Complete">
            <SetupComplete />
          </DocumentTitle>
        </JourneyRoute>
        <JourneyRoute
          path="/security/consent/esign"
          exact
          screens={[InformationScreenType.ESign]}
        >
          <DocumentTitle title="E-Sign">
            <ESignWrapper />
          </DocumentTitle>
        </JourneyRoute>
        <JourneyRoute
          path="/security/consent/obsa"
          exact
          screens={[InformationScreenType.OBSA]}
        >
          <DocumentTitle title="OBSA">
            <OBSAWrapper />
          </DocumentTitle>
        </JourneyRoute>
        <JourneyRoute
          path="/security/send-code"
          exact
          screens={['SendCode', 'MFASuppressionNoOptions']}
        >
          <DocumentTitle title="Send Code">
            <SendCodeWrapper />
          </DocumentTitle>
        </JourneyRoute>
        <JourneyRoute
          path="/security/verify-code"
          exact
          screens={['VerifyCode']}
        >
          <DocumentTitle title="Verify Code">
            <VerifyCodeWrapper />
          </DocumentTitle>
        </JourneyRoute>
        <JourneyRoute
          exact
          path="/security/new-password"
          screens={['NewPassword']}
        >
          <DocumentTitle title="New Password">
            <NewPassword />
          </DocumentTitle>
        </JourneyRoute>
        <JourneyRoute
          exact
          path="/security/password-updated"
          screens={[InformationScreenType.PasswordUpdated]}
        >
          <DocumentTitle title="Password Updated">
            <PasswordUpdated />
          </DocumentTitle>
        </JourneyRoute>
        <JourneyRoute
          exact
          path="/security/forgot-username/confirm-identity"
          screens={['ConfirmSSN']}
        >
          <DocumentTitle title="Forgot Username">
            <ConfirmSSN />
          </DocumentTitle>
        </JourneyRoute>
        <JourneyRoute
          exact
          path="/security/forgot-username/select-account"
          screens={['SelectAccount']}
        >
          <DocumentTitle title="Select Account">
            <SelectAccount />
          </DocumentTitle>
        </JourneyRoute>
        <JourneyRoute
          exact
          path="/security/forgot-username/username"
          screens={['ForgotUsernameConfirmation']}
        >
          <DocumentTitle title="Username">
            <ForgotUsernameConfirmation />
          </DocumentTitle>
        </JourneyRoute>
        <JourneyRoute
          exact
          path="/security/account-linking/bank-to-auto"
          screens={['BankToAutoLinking']}
        >
          <DocumentTitle title="Let's link up">
            <BankToAutoLinking />
          </DocumentTitle>
        </JourneyRoute>

        <JourneyRoute
          exact
          path="/security/account-linking/auto-link-failed-max-retries"
          screens={['AutoLinkFailedMaxRetries']}
        >
          <DocumentTitle title="We Couldn't Link Your Accounts">
            <AutoLinkFailedMaxRetries />
          </DocumentTitle>
        </JourneyRoute>

        <JourneyRoute
          exact
          path="/security/account-linking/select-account-type"
          screens={['LoginAccountTypeSelector']}
        >
          <DocumentTitle title="Select Account Type">
            <LoginAccountTypeSelector />
          </DocumentTitle>
        </JourneyRoute>
        <JourneyRoute
          exact
          path="/security/account-linking/auto-to-bank"
          screens={['AutoToBankLinking']}
        >
          <DocumentTitle title="Let's link up">
            <AutoToBankLinking />
          </DocumentTitle>
        </JourneyRoute>
        <JourneyRoute
          exact
          path="/security/account-linking/add-auto-account"
          screens={['AddAutoAccount']}
        >
          <DocumentTitle title="We're adding your new account">
            <AddAutoAccount />
          </DocumentTitle>
        </JourneyRoute>
        <JourneyRoute
          exact
          path="/security/account-linking/auto-success"
          screens={['AddAutoAccountSuccess']}
        >
          <DocumentTitle title="You're ready to hit the road">
            <AddAutoAccountSuccess />
          </DocumentTitle>
        </JourneyRoute>
        <JourneyRoute
          exact
          path="/security/account-linking/update-username"
          screens={['UpdateUsername']}
        >
          <DocumentTitle title="Update your username">
            <UpdateUsername />
          </DocumentTitle>
        </JourneyRoute>
        <JourneyRoute
          exact
          path="/security/account-linking/auto-failure"
          screens={['AddAutoAccountFailure']}
        >
          <DocumentTitle title="We can't add your account">
            <AddAutoAccountFailure />
          </DocumentTitle>
        </JourneyRoute>
        <JourneyRoute
          exact
          path="/security/account-linking/forgot-credentials"
          screens={['AutoForgotUsernamePassword']}
        >
          <DocumentTitle title="Almost there">
            <AutoForgotUsernamePassword />
          </DocumentTitle>
        </JourneyRoute>
        <JourneyRoute
          exact
          path="/security/account-linking/success"
          screens={['LinkingSuccess']}
        >
          <DocumentTitle title="You're linked">
            <LinkingSuccess />
          </DocumentTitle>
        </JourneyRoute>

        <JourneyRoute
          exact
          path="/security/account-linking/failure"
          screens={['AddAutoAccountGenericFailure']}
        >
          <DocumentTitle title="We couldn't add your account.">
            <AddAutoAccountGenericFailure />
          </DocumentTitle>
        </JourneyRoute>

        <JourneyRoute
          exact
          path="/security/auto-mfa-max-retries"
          screens={['AutoAltMfaMaxRetries']}
        >
          <DocumentTitle title="Call us">
            <AutoAltMfaMaxRetries />
          </DocumentTitle>
        </JourneyRoute>

        <Route exact path="/temp-unavailable">
          <DocumentTitle title="Temporarily Unavailable">
            <TemporarilyUnavailable />
          </DocumentTitle>
        </Route>
        {/* ---------------------- INTERNAL APP ROUTES ------------------------ */}
        {children}
      </Switch>
    </RedirectContext.Provider>
  )
}
